import { Injectable, inject } from '@angular/core';

import { BannersConfig } from '@frontend/sports/common/client-config-data-access';
import { ExpiringCache, ExpiringCacheFactory } from '@frontend/sports/common/core/utils/expiring-cache';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UserService } from '@frontend/sports/user/feature';
import { DeviceService, WINDOW } from '@frontend/vanilla/core';
import { Observable, map, of } from 'rxjs';

import { BannerTrackingService } from './banner-tracking.service';
import { VymlGamesModel } from './banner.model';
import { BannersService } from './banners.service';
import { CrossSaleTracking } from './cross-sale-tracking.model';

@Injectable({ providedIn: 'root' })
export class VymlService {
    private cache: ExpiringCache;

    constructor(
        private user: UserService,
        private bannerConfig: BannersConfig,
        private bannerService: BannersService,
        private device: DeviceService,
        private trackingService: BannerTrackingService,
        private redirectService: RedirectHelperService,
        cacheFactory: ExpiringCacheFactory,
    ) {
        this.cache = cacheFactory.create(this.bannerConfig.virtuals.cachingTimeout);
    }

    isEnabled(): boolean {
        const userCountry = this.user.isAuthenticated ? this.user.country : this.user.geoCountry;

        return !this.isBlacklistedCountry(userCountry);
    }

    get(imageSize: number = 200): Observable<VymlGamesModel[]> {
        if (!this.isEnabled()) {
            return of([]);
        }

        return this.cache.getOrCreate('games', this.bannerService.getVirtualSportsforVyml()).pipe(
            map((games) =>
                games.map((game, index) => ({
                    ...game,
                    tilePosition: index,
                    imageSrc: game.imageSrc.includes('?') ? `${game.imageSrc}&w=${imageSize}` : `${game.imageSrc}?w=${imageSize}`,
                })),
            ),
        );
    }

    open(
        game: VymlGamesModel,
        section: CrossSaleTracking.GameSectionOnDemand | CrossSaleTracking.GameSectionVyml,
        tracking: { position: string; pageName: string } = { position: '', pageName: '' },
    ): void {
        if (!this.user.isAuthenticated) {
            this.redirectService.goToLogin({ appendReferrer: true });

            return;
        }

        this.trackingService.trackVymlLaunch({
            name: game.sportName,
            index: game.tilePosition,
            displayName: game.title,
            lobbyPosition: tracking.position,
            url: game.launchUrl,
            pageName: tracking.pageName,
            section,
        });

        if (this.device.isMobile) {
            this.redirectService.goToPage(game.launchUrl);
        } else {
            this._window.open(game.launchUrl, '_blank', 'width=800,height=600');
        }
    }

    private isBlacklistedCountry(country: string | null): boolean {
        if (!country) {
            return false;
        }

        return this.bannerConfig.virtuals.blackListedCountries.some((currentCountry) => currentCountry.toUpperCase() === country.toUpperCase());
    }

    private readonly _window = inject(WINDOW);
}
